import {I18n} from '@lingui/core';
import React, {PropsWithChildren} from 'react';
import {I18nProvider} from '@lingui/react';
import {QueryClient, QueryClientProvider} from 'react-query';
import {ReactQueryDevtools} from 'react-query/devtools';
import {BrowserRouter} from 'react-router-dom';

import {ResourceConfigurationProvider} from '../../hooks/resource-configuration/resource-configuration-provider';
import {AppContextProvider} from '../../hooks/app-context/app-context-provider';
import {AppThemeProvider} from '../../styles/app-theme-provider';
import {AppContext} from '../../types/state.types';
import {AppTheme} from '../../styles/app-theme';
import {ReduxProvider} from './redux-provider';
import {SnackbarProvider} from '../../components/snackbars/snackbars';

export type AppProvidersProps = PropsWithChildren<{
    i18n: I18n
    config: any // app-config.js is not typed in the boilerplate
    language: string
    basename: string
    appContext: Omit<AppContext, 'history'> // history is provided from BrowserRouterProvider
    theme: AppTheme
}>

const queryClient = new QueryClient();
// @ts-ignore for development
window.__queryClient = queryClient;

export const AppProviders = ({
    i18n,
    basename,
    appContext,
    config,
    language,
    theme,
    children
}: AppProvidersProps) => {
    return (
        <QueryClientProvider client={queryClient}>
            <I18nProvider i18n={i18n} language={language}>
                <BrowserRouter basename={basename}>
                    <AppContextProvider context={appContext}>
                        <ReduxProvider {...appContext}>
                            <ResourceConfigurationProvider
                                getConfigurationItem={appContext.getConfigurationItem}
                                configKeys={config.requiredConfigKeys}
                            >
                                <AppThemeProvider appTheme={theme}>
                                    <SnackbarProvider>
                                        {process.env.NODE_ENV === 'development' ?
                                            <ReactQueryDevtools
                                                initialIsOpen={false}/> : null}
                                        {children}
                                    </SnackbarProvider>
                                </AppThemeProvider>
                            </ResourceConfigurationProvider>
                        </ReduxProvider>
                    </AppContextProvider>
                </BrowserRouter>
            </I18nProvider>
        </QueryClientProvider>
    );
};
